/**
 * OmniClinicalBridge VR Clinical Simulation
 * OmniAlera Technologies Holdings Inc.
 * 
 * WebXR-based VR framework for clinical training
 * Compatible with Meta Quest, HTC Vive, and mobile VR viewers
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';

// VR Session state
export type VRSessionState = 
  | 'unsupported'
  | 'idle'
  | 'checking'
  | 'ready'
  | 'entering'
  | 'active'
  | 'paused'
  | 'exiting'
  | 'error';

// Simulation types
export type SimulationType = 
  | 'emergency-resuscitation'
  | 'iv-access'
  | 'intubation'
  | 'chest-drain'
  | 'lumbar-puncture'
  | 'suturing'
  | 'physical-exam'
  | 'patient-assessment';

// Simulation scenario
export interface VRScenario {
  id: string;
  type: SimulationType;
  title: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration: number; // minutes
  objectives: string[];
  prerequisites?: string[];
  equipment: string[];
}

// User performance
export interface PerformanceMetrics {
  scenarioId: string;
  startTime: Date;
  endTime?: Date;
  completionPercentage: number;
  objectivesCompleted: string[];
  mistakes: string[];
  timeToComplete?: number;
  score?: number;
  feedback: string[];
}

// Controller state
export interface ControllerState {
  hand: 'left' | 'right';
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number; w: number };
  grip: number;
  trigger: number;
  buttons: Record<string, boolean>;
}

// Context type
interface VRContextType {
  // State
  sessionState: VRSessionState;
  isVRSupported: boolean;
  currentScenario: VRScenario | null;
  performance: PerformanceMetrics | null;
  
  // Controllers
  leftController: ControllerState | null;
  rightController: ControllerState | null;
  
  // Session management
  checkVRSupport: () => Promise<boolean>;
  enterVR: () => Promise<boolean>;
  exitVR: () => void;
  
  // Scenario management
  loadScenario: (scenarioId: string) => Promise<boolean>;
  startScenario: () => void;
  pauseScenario: () => void;
  resumeScenario: () => void;
  endScenario: () => void;
  resetScenario: () => void;
  
  // Performance
  recordObjective: (objectiveId: string) => void;
  recordMistake: (mistake: string) => void;
  getPerformanceReport: () => PerformanceMetrics | null;
  
  // Error handling
  error: string | null;
}

const VRContext = createContext<VRContextType | undefined>(undefined);

// Available scenarios
export const VR_SCENARIOS: VRScenario[] = [
  {
    id: 'cpr-basic',
    type: 'emergency-resuscitation',
    title: 'Basic Life Support (BLS)',
    description: 'Practice adult CPR including chest compressions and rescue breathing.',
    difficulty: 'beginner',
    duration: 15,
    objectives: [
      'Assess responsiveness',
      'Call for help',
      'Check breathing',
      'Perform chest compressions (100-120/min)',
      'Maintain proper hand position',
      'Achieve adequate depth (5-6cm)',
      'Allow full recoil',
      'Minimize interruptions'
    ],
    equipment: ['CPR manikin', 'AED trainer']
  },
  {
    id: 'iv-peripheral',
    type: 'iv-access',
    title: 'Peripheral IV Cannulation',
    description: 'Practice inserting a peripheral IV line with proper technique.',
    difficulty: 'beginner',
    duration: 20,
    objectives: [
      'Gather equipment',
      'Hand hygiene',
      'Apply tourniquet',
      'Select appropriate vein',
      'Prepare insertion site',
      'Insert cannula at correct angle',
      'Confirm flashback',
      'Advance and secure',
      'Flush and document'
    ],
    equipment: ['IV cannula', 'Tourniquet', 'Antiseptic', 'Dressing']
  },
  {
    id: 'intubation-adult',
    type: 'intubation',
    title: 'Adult Endotracheal Intubation',
    description: 'Practice direct laryngoscopy and endotracheal tube placement.',
    difficulty: 'advanced',
    duration: 30,
    prerequisites: ['Basic airway management', 'BLS certification'],
    objectives: [
      'Position patient correctly',
      'Pre-oxygenate',
      'Select appropriate tube size',
      'Insert laryngoscope blade',
      'Visualize vocal cords',
      'Pass tube through cords',
      'Confirm placement',
      'Secure tube',
      'Verify with capnography'
    ],
    equipment: ['Laryngoscope', 'ET tubes', 'Stylet', 'Syringe', 'BVM']
  },
  {
    id: 'lumbar-puncture',
    type: 'lumbar-puncture',
    title: 'Lumbar Puncture Procedure',
    description: 'Practice sterile technique for diagnostic lumbar puncture.',
    difficulty: 'intermediate',
    duration: 25,
    objectives: [
      'Obtain informed consent',
      'Position patient',
      'Identify landmarks (L3-L4 or L4-L5)',
      'Sterile technique',
      'Local anesthesia',
      'Insert spinal needle',
      'Confirm CSF flow',
      'Measure opening pressure',
      'Collect samples',
      'Remove needle and dress'
    ],
    equipment: ['LP kit', 'Sterile drape', 'Manometer', 'Collection tubes']
  }
];

export const VRProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sessionState, setSessionState] = useState<VRSessionState>('checking');
  const [isVRSupported, setIsVRSupported] = useState(false);
  const [currentScenario, setCurrentScenario] = useState<VRScenario | null>(null);
  const [performance, setPerformance] = useState<PerformanceMetrics | null>(null);
  const [leftController, _setLeftController] = useState<ControllerState | null>(null);
  const [rightController, _setRightController] = useState<ControllerState | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const xrSession = useRef<any>(null);
  const animationFrame = useRef<number | null>(null);
  
  // Check VR support on mount
  useEffect(() => {
    checkVRSupport();
  }, []);
  
  // Check if WebXR is supported
  const checkVRSupport = useCallback(async (): Promise<boolean> => {
    setSessionState('checking');
    
    if (!('xr' in navigator)) {
      console.log('[VR] WebXR not available');
      setIsVRSupported(false);
      setSessionState('unsupported');
      return false;
    }
    
    try {
      const supported = await (navigator as any).xr.isSessionSupported('immersive-vr');
      setIsVRSupported(supported);
      setSessionState(supported ? 'ready' : 'unsupported');
      console.log('[VR] Immersive VR supported:', supported);
      return supported;
    } catch (err) {
      console.error('[VR] Error checking support:', err);
      setIsVRSupported(false);
      setSessionState('unsupported');
      return false;
    }
  }, []);
  
  // Enter VR session
  const enterVR = useCallback(async (): Promise<boolean> => {
    if (!isVRSupported) {
      setError('VR is not supported on this device');
      return false;
    }
    
    try {
      setSessionState('entering');
      
      const session = await (navigator as any).xr.requestSession('immersive-vr', {
        requiredFeatures: ['local-floor'],
        optionalFeatures: ['hand-tracking', 'bounded-floor']
      });
      
      xrSession.current = session;
      
      session.addEventListener('end', () => {
        setSessionState('idle');
        xrSession.current = null;
      });
      
      setSessionState('active');
      console.log('[VR] Session started');
      
      // Start render loop (would integrate with Three.js/A-Frame)
      // startRenderLoop(session);
      
      return true;
    } catch (err) {
      console.error('[VR] Failed to enter VR:', err);
      setError('Failed to start VR session');
      setSessionState('error');
      return false;
    }
  }, [isVRSupported]);
  
  // Exit VR session
  const exitVR = useCallback(() => {
    if (xrSession.current) {
      xrSession.current.end();
      xrSession.current = null;
    }
    
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
    
    setSessionState('idle');
  }, []);
  
  // Load scenario
  const loadScenario = useCallback(async (scenarioId: string): Promise<boolean> => {
    const scenario = VR_SCENARIOS.find(s => s.id === scenarioId);
    
    if (!scenario) {
      setError(`Scenario not found: ${scenarioId}`);
      return false;
    }
    
    setCurrentScenario(scenario);
    setPerformance({
      scenarioId,
      startTime: new Date(),
      completionPercentage: 0,
      objectivesCompleted: [],
      mistakes: [],
      feedback: []
    });
    
    console.log('[VR] Scenario loaded:', scenario.title);
    return true;
  }, []);
  
  // Start scenario
  const startScenario = useCallback(() => {
    if (performance) {
      setPerformance({
        ...performance,
        startTime: new Date()
      });
    }
    console.log('[VR] Scenario started');
  }, [performance]);
  
  // Pause scenario
  const pauseScenario = useCallback(() => {
    setSessionState('paused');
  }, []);
  
  // Resume scenario
  const resumeScenario = useCallback(() => {
    setSessionState('active');
  }, []);
  
  // End scenario
  const endScenario = useCallback(() => {
    if (performance && currentScenario) {
      const endTime = new Date();
      const timeToComplete = (endTime.getTime() - performance.startTime.getTime()) / 1000 / 60; // minutes
      
      // Calculate score
      const objectiveCount = currentScenario.objectives.length;
      const completedCount = performance.objectivesCompleted.length;
      const mistakePenalty = performance.mistakes.length * 5;
      const score = Math.max(0, Math.round((completedCount / objectiveCount) * 100 - mistakePenalty));
      
      setPerformance({
        ...performance,
        endTime,
        timeToComplete,
        score,
        completionPercentage: Math.round((completedCount / objectiveCount) * 100),
        feedback: generateFeedback(performance, currentScenario, score)
      });
    }
    
    exitVR();
  }, [performance, currentScenario, exitVR]);
  
  // Reset scenario
  const resetScenario = useCallback(() => {
    if (currentScenario) {
      setPerformance({
        scenarioId: currentScenario.id,
        startTime: new Date(),
        completionPercentage: 0,
        objectivesCompleted: [],
        mistakes: [],
        feedback: []
      });
    }
  }, [currentScenario]);
  
  // Record completed objective
  const recordObjective = useCallback((objectiveId: string) => {
    if (performance) {
      setPerformance({
        ...performance,
        objectivesCompleted: [...performance.objectivesCompleted, objectiveId]
      });
    }
  }, [performance]);
  
  // Record mistake
  const recordMistake = useCallback((mistake: string) => {
    if (performance) {
      setPerformance({
        ...performance,
        mistakes: [...performance.mistakes, mistake]
      });
    }
  }, [performance]);
  
  // Get performance report
  const getPerformanceReport = useCallback((): PerformanceMetrics | null => {
    return performance;
  }, [performance]);
  
  const value: VRContextType = {
    sessionState,
    isVRSupported,
    currentScenario,
    performance,
    leftController,
    rightController,
    checkVRSupport,
    enterVR,
    exitVR,
    loadScenario,
    startScenario,
    pauseScenario,
    resumeScenario,
    endScenario,
    resetScenario,
    recordObjective,
    recordMistake,
    getPerformanceReport,
    error
  };
  
  return (
    <VRContext.Provider value={value}>
      {children}
    </VRContext.Provider>
  );
};

export const useVR = (): VRContextType => {
  const context = useContext(VRContext);
  if (!context) {
    throw new Error('useVR must be used within a VRProvider');
  }
  return context;
};

// Generate feedback based on performance
function generateFeedback(
  performance: PerformanceMetrics,
  scenario: VRScenario,
  score: number
): string[] {
  const feedback: string[] = [];
  
  if (score >= 90) {
    feedback.push('Excellent performance! You demonstrated strong competency in this procedure.');
  } else if (score >= 70) {
    feedback.push('Good performance. Review the areas below for improvement.');
  } else if (score >= 50) {
    feedback.push('Satisfactory performance. Additional practice is recommended.');
  } else {
    feedback.push('This procedure requires more practice. Review the training materials and try again.');
  }
  
  // Identify missed objectives
  const missedObjectives = scenario.objectives.filter(
    obj => !performance.objectivesCompleted.includes(obj)
  );
  
  if (missedObjectives.length > 0) {
    feedback.push(`Missed steps: ${missedObjectives.join(', ')}`);
  }
  
  if (performance.mistakes.length > 0) {
    feedback.push(`Areas for improvement: ${performance.mistakes.join(', ')}`);
  }
  
  return feedback;
}
